import type { ReactNode } from 'react';
import ui from '../styles/ui.module.css';
import styles from './PresetPicker.module.css';

export interface PresetOption<T extends string> {
  id: T;
  label: string;
  icon: ReactNode;
  /** Shown as the hint instead of the label when the option is disabled (e.g. no accompaniment). */
  disabledHint?: string;
}

interface Props<T extends string> {
  /** The group's label, followed by the name of the current preset. */
  label: string;
  labelId: string;
  /** The preset the mixer matches, or null when it matches none. */
  current: T | null;
  currentLabel: string;
  options: readonly PresetOption<T>[];
  onChoose: (id: T) => void;
}

/**
 * Mixer presets as one strip of icon buttons ("What to play" in practice, the part groups in
 * rehearsal mode). Labels show as hints on hover and keyboard focus.
 */
export function PresetPicker<T extends string>({ label, labelId, current, currentLabel, options, onChoose }: Props<T>) {
  return (
    <div className={styles.group}>
      <span className={ui.label} id={labelId}>
        {label}: <span className={styles.current}>{currentLabel}</span>
      </span>
      <div className={styles.segmented} role="radiogroup" aria-labelledby={labelId}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={current === o.id}
            aria-label={o.label}
            data-tip={o.disabledHint ?? o.label}
            className={`${styles.segment} ${current === o.id ? styles.segmentOn : ''}`}
            disabled={o.disabledHint !== undefined}
            onClick={() => onChoose(o.id)}
          >
            {o.icon}
          </button>
        ))}
      </div>
    </div>
  );
}
